import React, { useRef, useState } from 'react'
import ReactSelect from 'react-select'

import { FormLabel, notEmpty, useIsClient } from '../../../module'
import ValueContainer from './CustomSelectComponents/ValueContainer'
import Option from './CustomSelectComponents/Option'
import MenuList from './CustomSelectComponents/MenuList'
import DropdownIndicator from './CustomSelectComponents/DropdownIndicator'
import {
  getStyles,
  type SelectGroupType,
  useBottomEdgeProximityDetector,
  useClickOutOfSelect,
  useRightEdgeDetector,
} from './SelectHelpers'
import GroupHeading from './CustomSelectComponents/GroupHeading'
import { type SearchBarProps } from '../../SearchBar/SearchBar'
import { c } from '../../../translations/LibraryTranslationService'

export type SelectProps<OptionType> = {
  /** Options to display in the dropdown menu */
  options: OptionType[] | SelectGroupType<OptionType>[]
  /** Key that will determine the value of the selected option */
  optionKeyName: keyof OptionType
  /** Key that will determine what to display as an option */
  labelKeyName: keyof OptionType
  /** The currently selected option state */
  selectedItem: OptionType
  /** Function to call when the selected option changes */
  onChange: (option: OptionType) => void
  /** Optional function to call when removing focus (onBlur) */
  onBlur?: (option: OptionType) => void
  /** Optional function to call when opening the dropdown menu */
  onFocus?: () => void
  /** Optional props to pass to the FormLabel component */
  labelProps?: Pick<
    React.ComponentProps<typeof FormLabel>,
    'label' | 'rightLabel' | 'tooltip' | 'id'
  >
  /** Overrides message displayed in dropdown when there are no options */
  noOptionsMessage?: string
  /** Turns the select red if in an error state */
  error?: boolean
  /** Turns the select gray and prevents interaction */
  disabled?: boolean
  /** Optional prop to display loading state */
  loading?: boolean
  /** Adds an asterisk to the label to indicate the field is required */
  required?: boolean
  /** Optionally removes the border around the Select */
  noBorder?: boolean
  /** Optionally add more configuration for the SearchBar */
  searchBarProps?: Omit<SearchBarProps, 'value' | 'onChange'> & {
    /** Optionally show the SearchBar at all times instead of the current conditional rendering */
    showSearchBar?: boolean
    /** Optional function that will make a callout with the search `value` passed back */
    onChange?: (value: string) => void
  }
  /** Opt out of using the portal for the menu. This is needed whenever the Select needs to be used in a Popover. */
  noMenuPortal?: boolean
  /** Optional placeholder when there is no selected option */
  placeholder?: string
  /** Optional prop to allow a Button in the footer */
  footerButtonProps?: {
    /** Function to call when creating a new option */
    callout: () => void
    /** Text to display on the button */
    text: string
  }
  /** Optionally override the width of the Select menu */
  menuWidth?: number
  /** Optionally add configuration for infinite scroll */
  scrollProps?: {
    /** Flag to determine if the select has more data that needs to be called */
    hasMore: boolean
    /** With pagination, new data needs to get called every time we reach the scrolling threshold. This is the function to get the next set of data. */
    getData: () => void
  }
  /** Optional prop to add a test id to the Select for QA testing */
  qaTestId?: string
}

const Select = <OptionType,>({
  options,
  labelProps,
  selectedItem,
  onChange,
  onBlur,
  onFocus,
  error,
  disabled,
  required,
  optionKeyName,
  labelKeyName,
  loading,
  noBorder,
  noOptionsMessage,
  searchBarProps,
  noMenuPortal,
  placeholder,
  footerButtonProps,
  menuWidth,
  scrollProps,
  qaTestId = 'select',
}: SelectProps<OptionType>): React.JSX.Element => {
  const [isFocused, setIsFocused] = useState(false),
    [inputValue, setInputValue] = useState(''),
    containerRef = useClickOutOfSelect({ setIsFocused, setInputValue }),
    menuHeightOffset =
      16 + // 8px padding top and bottom
      (searchBarProps ? 40 : 0), // 40px for the SearchBar
    menuHeight = 40 * options?.length + menuHeightOffset,
    maxMenuHeight = searchBarProps ? 350 : 300, // 300px is the max height of the ScrollingContainer that we are using. 50px is the height of the SearchBar and surrounding padding.
    menuHeightTotalCalculation =
      menuHeight > maxMenuHeight ? maxMenuHeight : menuHeight,
    containerNearBottom = useBottomEdgeProximityDetector(
      containerRef,
      menuHeightTotalCalculation,
    ),
    menuRef = useRef<HTMLDivElement>(null),
    isOffscreenRight = useRightEdgeDetector(menuRef, isFocused),
    isClient = useIsClient()

  const onSelectChange = (option: OptionType | null) => {
    if (!option) return
    onChange(option)
    setIsFocused(false)
  }

  const onBlurCallout = () => {
    onBlur?.(selectedItem)
  }

  return (
    <div ref={containerRef} qa-test-id={qaTestId}>
      {labelProps ? (
        <FormLabel
          {...labelProps}
          error={error}
          disabled={disabled}
          required={required}
        />
      ) : null}
      <div>
        <ReactSelect
          menuPlacement={containerNearBottom ? 'top' : 'auto'}
          options={options}
          onMenuInputFocus={() => setIsFocused(true)}
          value={selectedItem}
          onChange={(value) => onSelectChange(value as OptionType)}
          onBlur={() => onBlurCallout()}
          onFocus={onFocus}
          {...(!noMenuPortal && isClient
            ? { menuPortalTarget: document.body }
            : {})}
          inputValue={inputValue}
          onInputChange={(val) => setInputValue(val)}
          isDisabled={disabled}
          error={error}
          noBorder={noBorder}
          required={required}
          isLoading={loading}
          isSearchable={false}
          styles={getStyles<OptionType>()}
          optionKeyName={optionKeyName}
          labelKeyName={labelKeyName}
          searchBarProps={searchBarProps}
          getOptionLabel={(option) => option[labelKeyName] as string}
          noOptionsMessage={() => noOptionsMessage ?? c('noResultsFound')}
          placeholder={placeholder}
          {...(footerButtonProps ? { footerButtonProps } : {})}
          components={{
            IndicatorSeparator: null,
            LoadingIndicator: () => null,
            DropdownIndicator,
            MenuList,
            Option,
            ValueContainer,
            GroupHeading,
          }}
          // The goal here is to prevent the select menu from closing when the spacebar is pressed.
          onKeyDown={(e) => {
            if (e.key === ' ' && e.target instanceof HTMLElement) {
              const target = e.target as HTMLInputElement
              const isReactSelectElement = e.target.id.includes('react-select')
              const isEmptyInput = !notEmpty(target.value) // This check is specifically for the SearchBar. If the SearchBar is empty, we want to prevent the menu from closing when pressing space.
              if (isReactSelectElement || isEmptyInput) {
                e.preventDefault()
                e.stopPropagation()
              }
            }
          }}
          isOffscreenRight={isOffscreenRight}
          menuRef={menuRef as React.RefObject<HTMLDivElement>}
          menuWidth={menuWidth}
          // This is needed to have the SearchBar work properly. Without this, the menu will close when trying to interact with the SearchBar.
          {...(isFocused ? { menuIsOpen: true, isFocused: true } : {})}
          hasMore={scrollProps?.hasMore}
          getData={scrollProps?.getData}
        />
      </div>
    </div>
  )
}

export default Select
